import React from 'react';

import { T } from '@transifex/react';

import { Badge } from '@/components/ui/badge';

/**
 * Displays the active property's backend-derived role. Mirrors the pools
 * `ViewerRoleBadge` (`pools/components/badges.tsx`): a shadcn <Badge> label.
 *
 * The property-role enum isn't frozen on the FE yet, so known roles get a
 * Transifex-translated label via KNOWN_ROLE_LABELS and anything else falls back
 * to a humanized (snake_case → Title Case) label — so a new backend role never
 * renders blank or crashes. Keys match the backend `ContactRole` enum
 * (uppercase, display-only).
 */
const KNOWN_ROLE_LABELS: Record<string, React.ReactNode> = {
  OWNER: <T _str="Owner" />,
  CO_OWNER: <T _str="Co-owner" />,
  ADMINISTRATOR: <T _str="Administrator" />,
  PAYER: <T _str="Payer" />,
  CONTACT: <T _str="Contact" />,
  TENANT: <T _str="Tenant" />,
  PROPERTY_MANAGER: <T _str="Property manager" />,
  CARETAKER: <T _str="Caretaker" />,
};

const humanize = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const RoleBadge: React.FC<{ role?: string | null }> = ({ role }) => {
  if (!role) return null;
  return (
    <Badge variant="secondary">
      {KNOWN_ROLE_LABELS[role] ?? humanize(role)}
    </Badge>
  );
};
